import { useMemo, memo } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
} from 'chart.js'
import { Bar, Doughnut } from 'react-chartjs-2'
import { useHistory } from 'react-router-dom'
import {
  BUYER_ORDER_UNPAID,
  HOST_ORDER_VERIFIED,
  HOST_ORDER_COLLECTED,
  BUYER_ORDER_PAID,
  HOST_ORDER_SUBMITTED_VERIFIED,
  HOST_ORDER_SUBMITTED_COLLECTED
} from '_utils/constant'
import useDashboardRedirect from '../hooks/useDashboardRedirect'
import { COLOR_PANEL } from '../../../utils/constant'
import { formatNumberEng } from '../helper/formatNumber'

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip)

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '75%',
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: true,
    },
  },
}

const MAX_LABLE_CHART = 10

function Analytics({ eventReport, loading, isViewAll, filters, eventTimeRange }) {
  const history = useHistory()
  const { redirectToManageOrders } = useDashboardRedirect()

  const rawData = eventReport?.eventStatus || []

  const filteredEvents = useMemo(
    () => {
      const validEvents = rawData.filter((item) => (item.totalSales || 0) > 0)
      return [...validEvents].sort((a, b) => Number(b.active) - Number(a.active))
    },
    [rawData]
  )

  const labels = useMemo(() => filteredEvents.map((item) =>
      (item.eName.length > MAX_LABLE_CHART ? `${item.eName.substring(0, MAX_LABLE_CHART)}...` : item.eName)), [filteredEvents])

  const dataValues = useMemo(
    () => filteredEvents.map((item) => item.totalSales),
    [filteredEvents]
  )

  const backgroundColors = useMemo(
    () => filteredEvents.map((item) => (item.active ? COLOR_PANEL.TEAL : COLOR_PANEL.AMBER)),
    [filteredEvents]
  )

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: dataValues,
          backgroundColor: backgroundColors,
          borderSkipped: false,
          maxBarThickness: 32,
        },
      ],
    }),
    [labels, dataValues, backgroundColors]
  )

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: (event, elements) => {
        if (isViewAll) return
        const eId = filteredEvents[elements?.[0]?.index]?.eId
        if (eId) {
          redirectToManageOrders({
            eventId: eId,
            dateRangeOrder: { start: filters?.start, end: filters?.end },
            dateRangeEvent: eventTimeRange,
            status: filters?.status,
          })
        }
      },
      onHover: (event, elements) => {
        const target = event.native?.target || event.target
        if (target) target.style.cursor = (elements.length && !isViewAll) ? 'pointer' : 'default'
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          backgroundColor: COLOR_PANEL.TOOLTIP_BG,
          titleColor: COLOR_PANEL.WHITE,
          bodyColor: COLOR_PANEL.WHITE,
          padding: 8,
          borderRadius: 4,
          displayColors: false,
          callbacks: {
            title: () => '',
            label: (context) => {
              const item = filteredEvents[context.dataIndex]
              return `${item.eName} · $${formatNumberEng(context.raw, 2)} (${item.active ? 'Active' : 'Closed'})`
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            color: COLOR_PANEL.MID,
            font: {
              size: 9,
              family: '-apple-system, sans-serif',
            },
            maxRotation: 45,
            minRotation: 45,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: COLOR_PANEL.GRID,
            drawBorder: false,
            borderDash: [5, 5],
          },
          ticks: {
            color: COLOR_PANEL.MID,
            font: {
              size: 9,
              family: '-apple-system, sans-serif',
            },
            stepSize: dataValues.length > 0 ? Math.ceil(Math.max(...dataValues, 10) / 4) : 5,
            callback(value) {
              return value === 0 ? '$0' : `$${formatNumberEng(value, 2)}`
            },
          },
        },
      },
    }),
    [
      filteredEvents,
      dataValues,
      history,
      isViewAll,
      redirectToManageOrders,
      filters,
      eventTimeRange,
    ]
  )

  // Logic caculate Payment Status Doughnut Chart
  const paymentDataCalculated = useMemo(() => {
    const paymentStatus = eventReport?.paymentStatus || {}
    const verified = paymentStatus.verify || 0
    const pending = paymentStatus.pendingReview || 0
    const unpaid = paymentStatus.unpaid || 0
    const total = verified + pending + unpaid

    const verifiedPct = total > 0 ? Math.round((verified / total) * 100) : 0
    const pendingPct = total > 0 ? Math.round((pending / total) * 100) : 0
    const unpaidPct = total > 0 ? Math.max(0, 100 - verifiedPct - pendingPct) : 0

    const doughnutData = {
      labels: ['Verified & Paid', 'Pending Review', 'Unpaid'],
      datasets: [
        {
          data: [verified, pending, unpaid],
          backgroundColor: [COLOR_PANEL.TEAL, COLOR_PANEL.AMBER, COLOR_PANEL.RED],
          borderWidth: 0,
        },
      ],
    }

    return {
      verified,
      pending,
      unpaid,
      total,
      verifiedPct,
      pendingPct,
      unpaidPct,
      doughnutData,
    }
  }, [eventReport?.paymentStatus])

  const { verified, pending, unpaid, total, verifiedPct, pendingPct, unpaidPct, doughnutData } =
    paymentDataCalculated

  return (
    <div className='analytics-section'>
      <div className='section-label'>Analytics</div>
      <div className='chart-grid'>
        {/* Orders bar chart */}
        <div className='chart-card' style={{ minWidth: 0 }}>
          <div className='chart-header'>
            <div>
              <div className='chart-title'>Events Sales</div>
              <div className='chart-sub'>Sales amount per event (SGD)</div>
            </div>
          </div>

          {/* Render canvas Chart.js */}
          <div
            style={{
              height: '140px',
              marginTop: '10px',
              position: 'relative',
              width: '100%',
              minWidth: 0,
            }}
          >
            {loading ? (
              <div className='skeleton-loader' style={{ width: '100%', height: '100%' }} />
            ) : (
              <Bar data={chartData} options={chartOptions} />
            )}
          </div>
          <div className='chart-legend'>
            <div className='legend-item'>
              <div className='legend-marker closed'></div> Closed
            </div>
            <div className='legend-item'>
              <div className='legend-marker active'></div> Active
            </div>
          </div>
        </div>

        {/* Payment status donut */}
        <div className='chart-card' style={{ minWidth: 0 }}>
          <div className='chart-header'>
            <div>
              <div className='chart-title'>Payment Status</div>
              <div className='chart-sub'>Across all open events</div>
            </div>
          </div>
          <div className='donut-wrap'>
            {loading ? (
              <>
                <svg className='donut-svg' width='130' height='130' viewBox='0 0 130 130'>
                  <circle
                    cx='65'
                    cy='65'
                    r='45'
                    fill='none'
                    stroke='var(--teal-light)'
                    strokeWidth='18'
                    style={{ opacity: 0.5 }}
                  />
                  <circle
                    cx='65'
                    cy='65'
                    r='45'
                    fill='none'
                    stroke='var(--faint)'
                    strokeWidth='18'
                    strokeDasharray='70 282.74'
                    strokeDashoffset='0'
                    transform='rotate(-90 65 65)'
                    style={{
                      opacity: 0.25,
                      animation: 'loading-shimmer 1.4s ease-in-out infinite',
                    }}
                  />
                  <text
                    x='65'
                    y='68'
                    textAnchor='middle'
                    style={{
                      fontSize: '11px',
                      fill: 'var(--faint)',
                      fontWeight: 500,
                      fontFamily: '-apple-system,sans-serif',
                    }}
                  >
                    Loading...
                  </text>
                </svg>
                <div className='legend' style={{ padding: '0 4px' }}>
                  {[1, 2, 3].map((i) => (
                    <div key={i} className='legend-item' style={{ height: '18px' }}>
                      <div
                        className='legend-label'
                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <div
                          className='legend-dot'
                          style={{ background: 'var(--border-light)' }}
                        ></div>
                        <span
                          className='skeleton-loader'
                          style={{ width: '80px', height: '10px' }}
                        />
                      </div>
                      <div className='legend-val'>
                        <span
                          className='skeleton-loader'
                          style={{ width: '40px', height: '10px' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className='donut-chart-container'>
                  <Doughnut data={doughnutData} options={doughnutOptions} />
                  <div
                    className={`donut-center-info ${isViewAll ? 'disabled-legend' : ''}`}
                    onClick={() => {
                      if (isViewAll) return
                      redirectToManageOrders({
                        dateRangeOrder: { start: filters?.start, end: filters?.end },
                        dateRangeEvent: eventTimeRange,
                        eventId: filters?.eid,
                        status: filters?.status,
                      })
                    }}
                  >
                    <span className='info-value'>{total}</span>
                    <span className='info-label'>total orders</span>
                  </div>
                </div>
                <div className='legend' style={{ padding: '0 4px' }}>
                  <div
                    className={`legend-item ${isViewAll ? 'disabled-legend' : ''}`}
                    onClick={() => {
                      if (isViewAll) return
                      redirectToManageOrders({
                        paymentStatus: [
                          { value: HOST_ORDER_VERIFIED, label: 'Verified', type: 'host', includeSubmitted: HOST_ORDER_SUBMITTED_VERIFIED },
                          { value: HOST_ORDER_COLLECTED, label: 'Collected', type: 'host', includeSubmitted: HOST_ORDER_SUBMITTED_COLLECTED },
                        ],
                        dateRangeOrder: { start: filters?.start, end: filters?.end },
                        dateRangeEvent: eventTimeRange,
                        eventId: filters?.eid,
                      })
                    }}
                  >
                    <div className='legend-label'>
                      <div className='legend-dot' style={{ background: 'var(--teal)' }}></div>
                      Verified / Paid
                    </div>
                    <div className='legend-val' style={{ color: 'var(--teal)' }}>
                      {verified}{' '}
                      <span style={{ fontSize: '10px', fontWeight: 400, color: 'var(--mid)' }}>
                        ({verifiedPct}%)
                      </span>
                    </div>
                  </div>
                  <div
                    className={`legend-item ${isViewAll ? 'disabled-legend' : ''}`}
                    onClick={() => {
                      if (isViewAll) return
                      redirectToManageOrders({
                        paymentStatus: [{ value: BUYER_ORDER_PAID, label: 'Pending Review', isPending: true }],
                        dateRangeOrder: { start: filters?.start, end: filters?.end },
                        dateRangeEvent: eventTimeRange,
                        eventId: filters?.eid,
                      })
                    }}
                  >
                    <div className='legend-label'>
                      <div className='legend-dot' style={{ background: 'var(--amber)' }}></div>
                      Pending Review
                    </div>
                    <div className='legend-val' style={{ color: 'var(--amber)' }}>
                      {pending}{' '}
                      <span style={{ fontSize: '10px', fontWeight: 400, color: 'var(--mid)' }}>
                        ({pendingPct}%)
                      </span>
                    </div>
                  </div>
                  <div
                    className={`legend-item ${isViewAll ? 'disabled-legend' : ''}`}
                    onClick={() => {
                      if (isViewAll) return
                      redirectToManageOrders({
                        paymentStatus: [{ value: BUYER_ORDER_UNPAID, label: 'Not Paid' }],
                        dateRangeOrder: { start: filters?.start, end: filters?.end },
                        dateRangeEvent: eventTimeRange,
                        eventId: filters?.eid,
                      })
                    }}
                  >
                    <div className='legend-label'>
                      <div className='legend-dot' style={{ background: 'var(--red)' }}></div>
                      Unpaid
                    </div>
                    <div className='legend-val' style={{ color: 'var(--red)' }}>
                      {unpaid}{' '}
                      <span style={{ fontSize: '10px', fontWeight: 400, color: 'var(--mid)' }}>
                        ({unpaidPct}%)
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default memo(Analytics)
